import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Truck,
  MapPin,
  Phone,
  Navigation,
  CheckCircle,
  Clock,
  Package,
  User,
  FileText,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Play,
  Loader2,
  ExternalLink,
  Camera,
  X,
  Info,
  DollarSign,
  Wrench,
  Trash2
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { UNIT_TERMINAL_STATUSES, ITEM_TERMINAL_STATUSES } from '../utils/statusConstants';

// =====================================================
// DRIVER VIEW TRANSLATIONS - Inline to avoid import issues
// =====================================================
const driverTranslations = {
  loadingRoute: { en: 'Loading route...', es: 'Cargando ruta...', pt: 'Carregando rota...', zh: '加载路线中...', vi: 'Đang tải tuyến đường...', ko: '경로 로딩 중...', ar: 'جارٍ تحميل المسار...', fr: "Chargement de l'itinéraire...", hi: 'रूट लोड हो रहा है...', tl: 'Nilo-load ang ruta...', ru: 'Загрузка маршрута...', de: 'Route wird geladen...', ja: 'ルートを読み込み中...', it: 'Caricamento percorso...', pl: 'Ładowanie trasy...', uk: 'Завантаження маршруту...', tr: 'Rota yükleniyor...', th: 'กำลังโหลดเส้นทาง...', my: 'လမ်းကြောင်း ဖွင့်နေသည်...', am: 'መንገድ በመጫን ላይ...' },
  pleaseWait: { en: 'Please wait', es: 'Por favor espere', pt: 'Por favor aguarde', zh: '请稍候', vi: 'Vui lòng chờ', ko: '잠시 기다려주세요', ar: 'يرجى الانتظار', fr: 'Veuillez patienter', hi: 'कृपया प्रतीक्षा करें', tl: 'Mangyaring maghintay', ru: 'Пожалуйста, подождите', de: 'Bitte warten', ja: 'お待ちください', it: 'Attendere prego', pl: 'Proszę czekać', uk: 'Будь ласка, зачекайте', tr: 'Lütfen bekleyin', th: 'กรุณารอสักครู่', my: 'ခဏစောင့်ပါ', am: 'እባክዎ ይጠብቁ' },
  unableToLoad: { en: 'Unable to load route', es: 'No se pudo cargar la ruta', pt: 'Não foi possível carregar a rota', zh: '无法加载路线', vi: 'Không thể tải tuyến đường', ko: '경로를 불러올 수 없습니다', ar: 'تعذر تحميل المسار', fr: "Impossible de charger l'itinéraire", hi: 'रूट लोड करने में असमर्थ', tl: 'Hindi ma-load ang ruta', ru: 'Не удалось загрузить маршрут', de: 'Route konnte nicht geladen werden', ja: 'ルートを読み込めません', it: 'Impossibile caricare il percorso', pl: 'Nie można załadować trasy', uk: 'Не вдалося завантажити маршрут', tr: 'Rota yüklenemedi', th: 'ไม่สามารถโหลดเส้นทางได้', my: 'လမ်းကြောင်း ဖွင့်၍မရပါ', am: 'መንገድ መጫን አልተቻለም' },
  refreshPage: { en: 'Refresh Page', es: 'Actualizar página', pt: 'Atualizar página', zh: '刷新页面', vi: 'Tải lại trang', ko: '페이지 새로고침', ar: 'تحديث الصفحة', fr: 'Rafraîchir la page', hi: 'पेज रीफ्रेश करें', tl: 'I-refresh ang page', ru: 'Обновить страницу', de: 'Seite aktualisieren', ja: 'ページを更新', it: 'Aggiorna pagina', pl: 'Odśwież stronę', uk: 'Оновити сторінку', tr: 'Sayfayı yenile', th: 'รีเฟรชหน้า', my: 'စာမျက်နှာကို ပြန်ဖွင့်ပါ', am: 'ገጹን አድስ' },
  deliveryRoute: { en: 'Delivery Route', es: 'Ruta de entrega', pt: 'Rota de entrega', zh: '配送路线', vi: 'Tuyến giao hàng', ko: '배송 경로', ar: 'مسار التوصيل', fr: 'Itinéraire de livraison', hi: 'डिलीवरी रूट', tl: 'Ruta ng paghahatid', ru: 'Маршрут доставки', de: 'Lieferroute', ja: '配送ルート', it: 'Percorso di consegna', pl: 'Trasa dostawy', uk: 'Маршрут доставки', tr: 'Teslimat rotası', th: 'เส้นทางจัดส่ง', my: 'ပို့ဆောင်ရေးလမ်းကြောင်း', am: 'የማድረስ መንገድ' },
  deliveriesComplete: { en: 'deliveries complete', es: 'entregas completadas', pt: 'entregas concluídas', zh: '次配送已完成', vi: 'giao hàng hoàn tất', ko: '배송 완료', ar: 'عمليات تسليم مكتملة', fr: 'livraisons terminées', hi: 'डिलीवरी पूरी हुई', tl: 'paghahatid tapos na', ru: 'доставок выполнено', de: 'Lieferungen abgeschlossen', ja: '件の配送完了', it: 'consegne completate', pl: 'dostaw ukończonych', uk: 'доставок виконано', tr: 'teslimat tamamlandı', th: 'การจัดส่งเสร็จสิ้น', my: 'ပို့ဆောင်မှုများ ပြီးပါပြီ', am: 'አድረሳዎች ተጠናቅቀዋል' },
  live: { en: 'Live', es: 'En vivo', pt: 'Ao vivo', zh: '实时', vi: 'Trực tiếp', ko: '실시간', ar: 'مباشر', fr: 'En direct', hi: 'लाइव', tl: 'Live', ru: 'Онлайн', de: 'Live', ja: 'ライブ', it: 'In diretta', pl: 'Na żywo', uk: 'Наживо', tr: 'Canlı', th: 'สด', my: 'တိုက်ရိုက်', am: 'በቀጥታ' },
  dismiss: { en: 'Dismiss', es: 'Cerrar', pt: 'Fechar', zh: '关闭', vi: 'Đóng', ko: '닫기', ar: 'إغلاق', fr: 'Fermer', hi: 'बंद करें', tl: 'Isara', ru: 'Закрыть', de: 'Schließen', ja: '閉じる', it: 'Chiudi', pl: 'Zamknij', uk: 'Закрити', tr: 'Kapat', th: 'ปิด', my: 'ပိတ်မည်', am: 'ዝጋ' },
  startRoute: { en: 'Start Route', es: 'Iniciar ruta', pt: 'Iniciar rota', zh: '开始路线', vi: 'Bắt đầu tuyến đường', ko: '경로 시작', ar: 'بدء المسار', fr: "Démarrer l'itinéraire", hi: 'रूट शुरू करें', tl: 'Simulan ang ruta', ru: 'Начать маршрут', de: 'Route starten', ja: 'ルート開始', it: 'Inizia percorso', pl: 'Rozpocznij trasę', uk: 'Почати маршрут', tr: 'Rotayı başlat', th: 'เริ่มเส้นทาง', my: 'လမ်းကြောင်းစတင်ပါ', am: 'መንገድ ጀምር' },
  optimizingForTraffic: { en: 'Optimizing for Traffic...', es: 'Optimizando para el tráfico...', pt: 'Otimizando para o trânsito...', zh: '正在优化交通路线...', vi: 'Đang tối ưu hóa giao thông...', ko: '교통 상황에 맞게 최적화 중...', ar: 'جارٍ التحسين حسب حركة المرور...', fr: 'Optimisation pour le trafic...', hi: 'ट्रैफ़िक के लिए ऑप्टिमाइज़ हो रहा है...', tl: 'Ino-optimize para sa trapiko...', ru: 'Оптимизация маршрута...', de: 'Wird für Verkehr optimiert...', ja: '交通状況に最適化中...', it: 'Ottimizzazione per il traffico...', pl: 'Optymalizacja dla ruchu...', uk: 'Оптимізація для трафіку...', tr: 'Trafik için optimize ediliyor...', th: 'กำลังปรับเส้นทางตามสภาพจราจร...', my: 'ယာဉ်ကြောအတွက် ပိုကောင်းအောင်လုပ်နေသည်...', am: 'ለትራፊክ እየተመቻቸ ነው...' },
  routeOptimizedNote: { en: 'Route will be optimized for current traffic conditions', es: 'La ruta se optimizará según las condiciones actuales del tráfico', pt: 'A rota será otimizada para as condições atuais de trânsito', zh: '路线将根据当前交通状况进行优化', vi: 'Tuyến đường sẽ được tối ưu hóa theo tình trạng giao thông hiện tại', ko: '현재 교통 상황에 맞게 경로가 최적화됩니다', ar: 'سيتم تحسين المسار حسب ظروف المرور الحالية', fr: "L'itinéraire sera optimisé selon les conditions de circulation actuelles", hi: 'रूट वर्तमान ट्रैफ़िक स्थितियों के लिए ऑप्टिमाइज़ किया जाएगा', tl: 'Ang ruta ay io-optimize para sa kasalukuyang kondisyon ng trapiko', ru: 'Маршрут будет оптимизирован с учётом текущего трафика', de: 'Route wird für aktuelle Verkehrsbedingungen optimiert', ja: '現在の交通状況に合わせてルートが最適化されます', it: 'Il percorso sarà ottimizzato per le condizioni di traffico attuali', pl: 'Trasa zostanie zoptymalizowana pod kątem bieżącego ruchu', uk: 'Маршрут буде оптимізовано для поточного трафіку', tr: 'Rota mevcut trafik koşullarına göre optimize edilecektir', th: 'เส้นทางจะถูกปรับให้เหมาะกับสภาพจราจรปัจจุบัน', my: 'လမ်းကြောင်းကို လက်ရှိယာဉ်ကြောအတွက် ပိုကောင်းအောင်လုပ်ပါမည်', am: 'መንገድ ለአሁኑ ትራፊክ ሁኔታ ይመቻቻል' },
  noStops: { en: 'No stops on this route', es: 'No hay paradas en esta ruta', pt: 'Nenhuma parada nesta rota', zh: '此路线没有停靠点', vi: 'Không có điểm dừng trên tuyến đường này', ko: '이 경로에 정류장이 없습니다', ar: 'لا توجد محطات في هذا المسار', fr: 'Aucun arrêt sur cet itinéraire', hi: 'इस रूट पर कोई स्टॉप नहीं है', tl: 'Walang hintuan sa rutang ito', ru: 'Нет остановок на этом маршруте', de: 'Keine Stopps auf dieser Route', ja: 'このルートには停車地がありません', it: 'Nessuna fermata su questo percorso', pl: 'Brak przystanków na tej trasie', uk: 'Немає зупинок на цьому маршруті', tr: 'Bu rotada durak yok', th: 'ไม่มีจุดจอดบนเส้นทางนี้', my: 'ဤလမ်းကြောင်းတွင် ရပ်နားစရာ မရှိပါ', am: 'በዚህ መንገድ ላይ ማቆሚያ የለም' },
  partial: { en: 'Partial', es: 'Parcial', pt: 'Parcial', zh: '部分', vi: 'Một phần', ko: '부분', ar: 'جزئي', fr: 'Partiel', hi: 'आंशिक', tl: 'Bahagi', ru: 'Частичная', de: 'Teilweise', ja: '一部', it: 'Parziale', pl: 'Częściowa', uk: 'Часткова', tr: 'Kısmi', th: 'บางส่วน', my: 'တစ်စိတ်တစ်ပိုင်း', am: 'ከፊል' },
  itemsToDeliver: { en: (n) => `${n} item${n !== 1 ? 's' : ''} to deliver`, es: (n) => `${n} artículo${n !== 1 ? 's' : ''} a entregar`, pt: (n) => `${n} ite${n !== 1 ? 'ns' : 'm'} para entregar`, zh: (n) => `${n} 件物品待配送`, vi: (n) => `${n} mặt hàng cần giao`, ko: (n) => `${n}개 품목 배송`, ar: (n) => `${n} عنصر للتوصيل`, fr: (n) => `${n} article${n !== 1 ? 's' : ''} à livrer`, hi: (n) => `${n} आइटम डिलीवर करने हैं`, tl: (n) => `${n} item na ihahatid`, ru: (n) => `${n} товар(ов) к доставке`, de: (n) => `${n} Artikel zu liefern`, ja: (n) => `${n}件の配送品`, it: (n) => `${n} articol${n !== 1 ? 'i' : 'o'} da consegnare`, pl: (n) => `${n} przedmiot${n !== 1 ? 'ów' : ''} do dostawy`, uk: (n) => `${n} товар(ів) до доставки`, tr: (n) => `${n} ürün teslim edilecek`, th: (n) => `${n} รายการที่ต้องจัดส่ง`, my: (n) => `${n} ခု ပို့ဆောင်ရန်`, am: (n) => `${n} ዕቃ(ዎች) ለማድረስ` },
  setup: { en: 'Setup', es: 'Montaje', pt: 'Montagem', zh: '安装', vi: 'Lắp đặt', ko: '설치', ar: 'تركيب', fr: 'Installation', hi: 'सेटअप', tl: 'Setup', ru: 'Сборка', de: 'Aufbau', ja: 'セットアップ', it: 'Montaggio', pl: 'Montaż', uk: 'Збірка', tr: 'Kurulum', th: 'ติดตั้ง', my: 'တပ်ဆင်ခြင်း', am: 'ማዘጋጀት' },
  waste: { en: 'Waste', es: 'Residuos', pt: 'Resíduos', zh: '废料', vi: 'Rác', ko: '폐기물', ar: 'نفايات', fr: 'Déchets', hi: 'कचरा', tl: 'Basura', ru: 'Мусор', de: 'Abfall', ja: '廃棄物', it: 'Rifiuti', pl: 'Odpady', uk: 'Сміття', tr: 'Atık', th: 'ขยะ', my: 'အမှိုက်', am: 'ቆሻሻ' },
  preferredDeliveryTime: { en: 'Preferred Delivery Time', es: 'Hora de entrega preferida', pt: 'Horário de entrega preferido', zh: '首选送货时间', vi: 'Thời gian giao hàng ưa thích', ko: '선호 배송 시간', ar: 'وقت التسليم المفضل', fr: 'Heure de livraison préférée', hi: 'पसंदीदा डिलीवरी समय', tl: 'Gustong oras ng paghahatid', ru: 'Предпочтительное время доставки', de: 'Bevorzugte Lieferzeit', ja: '希望配送時間', it: 'Orario di consegna preferito', pl: 'Preferowany czas dostawy', uk: 'Бажаний час доставки', tr: 'Tercih edilen teslimat zamanı', th: 'เวลาจัดส่งที่ต้องการ', my: 'ပိုကြိုက်နှစ်သက်သော ပို့ဆောင်ချိန်', am: 'የሚመረጥ የማድረስ ጊዜ' },
  paymentStatus: { en: 'Payment Status', es: 'Estado del pago', pt: 'Status do pagamento', zh: '付款状态', vi: 'Trạng thái thanh toán', ko: '결제 상태', ar: 'حالة الدفع', fr: 'Statut du paiement', hi: 'भुगतान स्थिति', tl: 'Katayuan ng bayad', ru: 'Статус оплаты', de: 'Zahlungsstatus', ja: '支払い状況', it: 'Stato pagamento', pl: 'Status płatności', uk: 'Статус оплати', tr: 'Ödeme durumu', th: 'สถานะการชำระเงิน', my: 'ငွေပေးချေမှု အခြေအနေ', am: 'የክፍያ ሁኔታ' },
  orderTotal: { en: 'Order Total:', es: 'Total del pedido:', pt: 'Total do pedido:', zh: '订单总计:', vi: 'Tổng đơn hàng:', ko: '주문 합계:', ar: 'إجمالي الطلب:', fr: 'Total de la commande:', hi: 'ऑर्डर कुल:', tl: 'Kabuuang order:', ru: 'Итого заказа:', de: 'Gesamtbestellung:', ja: '注文合計:', it: 'Totale ordine:', pl: 'Suma zamówienia:', uk: 'Разом замовлення:', tr: 'Sipariş toplamı:', th: 'ยอดรวมคำสั่งซื้อ:', my: 'မှာယူမှုစုစုပေါင်း:', am: 'የትዕዛዝ ድምር:' },
  amountPaid: { en: 'Amount Paid:', es: 'Monto pagado:', pt: 'Valor pago:', zh: '已付金额:', vi: 'Số tiền đã trả:', ko: '지불 금액:', ar: 'المبلغ المدفوع:', fr: 'Montant payé:', hi: 'भुगतान राशि:', tl: 'Halagang binayaran:', ru: 'Оплачено:', de: 'Bezahlter Betrag:', ja: '支払い済み:', it: 'Importo pagato:', pl: 'Zapłacono:', uk: 'Сплачено:', tr: 'Ödenen tutar:', th: 'จำนวนที่ชำระแล้ว:', my: 'ပေးချေပြီးပမာဏ:', am: 'የተከፈለ መጠን:' },
  collectOnDelivery: { en: 'COLLECT ON DELIVERY:', es: 'COBRAR EN ENTREGA:', pt: 'COBRAR NA ENTREGA:', zh: '货到付款:', vi: 'THU TIỀN KHI GIAO:', ko: '배송 시 수금:', ar: 'التحصيل عند التسليم:', fr: 'PAIEMENT À LA LIVRAISON:', hi: 'डिलीवरी पर वसूल करें:', tl: 'KOLEKTAHIN SA PAGHAHATID:', ru: 'ПОЛУЧИТЬ ПРИ ДОСТАВКЕ:', de: 'BEI LIEFERUNG KASSIEREN:', ja: '配送時回収:', it: 'INCASSO ALLA CONSEGNA:', pl: 'POBRANIE PRZY DOSTAWIE:', uk: 'ОТРИМАТИ ПРИ ДОСТАВЦІ:', tr: 'TESLİMAT ÜCRETİ:', th: 'เก็บเงินปลายทาง:', my: 'ပို့ဆောင်ချိန်တွင်ကောက်ခံပါ:', am: 'በማድረስ ጊዜ ይሰብሰቡ:' },
  balanceDue: { en: 'Balance Due:', es: 'Saldo pendiente:', pt: 'Saldo devedor:', zh: '待付余额:', vi: 'Số dư còn lại:', ko: '미지불 잔액:', ar: 'الرصيد المستحق:', fr: 'Solde dû:', hi: 'बकाया राशि:', tl: 'Natitirang balanse:', ru: 'К оплате:', de: 'Offener Betrag:', ja: '残高:', it: 'Saldo dovuto:', pl: 'Do zapłaty:', uk: 'До сплати:', tr: 'Kalan borç:', th: 'ยอดค้างชำระ:', my: 'ကျန်ငွေ:', am: 'ቀሪ ክፍያ:' },
  paidInFull: { en: '✓ PAID IN FULL', es: '✓ PAGADO COMPLETO', pt: '✓ PAGO TOTAL', zh: '✓ 已全额支付', vi: '✓ ĐÃ THANH TOÁN ĐỦ', ko: '✓ 전액 결제 완료', ar: '✓ مدفوع بالكامل', fr: '✓ PAYÉ EN TOTALITÉ', hi: '✓ पूर्ण भुगतान', tl: '✓ BAYAD NA LAHAT', ru: '✓ ОПЛАЧЕНО ПОЛНОСТЬЮ', de: '✓ VOLLSTÄNDIG BEZAHLT', ja: '✓ 全額支払い済み', it: '✓ PAGATO PER INTERO', pl: '✓ ZAPŁACONO W CAŁOŚCI', uk: '✓ ОПЛАЧЕНО ПОВНІСТЮ', tr: '✓ TAMAMI ÖDENDI', th: '✓ ชำระเต็มจำนวนแล้ว', my: '✓ အပြည့်အဝပေးချေပြီး', am: '✓ ሙሉ ተከፍሏል' },
  itemsToDeliverHeader: { en: 'Items to Deliver', es: 'Artículos a entregar', pt: 'Itens para entregar', zh: '待配送物品', vi: 'Mặt hàng cần giao', ko: '배송 품목', ar: 'العناصر للتوصيل', fr: 'Articles à livrer', hi: 'डिलीवर करने के आइटम', tl: 'Mga item na ihahatid', ru: 'Товары к доставке', de: 'Zu liefernde Artikel', ja: '配送品', it: 'Articoli da consegnare', pl: 'Przedmioty do dostawy', uk: 'Товари до доставки', tr: 'Teslim edilecek ürünler', th: 'รายการที่ต้องจัดส่ง', my: 'ပို့ဆောင်ရမည့်ပစ္စည်းများ', am: 'የሚደረሱ ዕቃዎች' },
  partialOrder: { en: '(Partial Order)', es: '(Pedido parcial)', pt: '(Pedido parcial)', zh: '(部分订单)', vi: '(Đơn hàng một phần)', ko: '(부분 주문)', ar: '(طلب جزئي)', fr: '(Commande partielle)', hi: '(आंशिक ऑर्डर)', tl: '(Bahagi ng order)', ru: '(Частичный заказ)', de: '(Teilbestellung)', ja: '(一部注文)', it: '(Ordine parziale)', pl: '(Zamówienie częściowe)', uk: '(Часткове замовлення)', tr: '(Kısmi sipariş)', th: '(คำสั่งซื้อบางส่วน)', my: '(တစ်စိတ်တစ်ပိုင်းမှာယူမှု)', am: '(ከፊል ትዕዛዝ)' },
  verifyItem: { en: 'VERIFY ITEM #', es: 'VERIFICAR ARTÍCULO #', pt: 'VERIFICAR ITEM #', zh: '验证物品 #', vi: 'XÁC MINH MÃ HÀNG #', ko: '품목 확인 #', ar: 'تحقق من العنصر #', fr: 'VÉRIFIER ARTICLE #', hi: 'आइटम सत्यापित करें #', tl: 'I-VERIFY ANG ITEM #', ru: 'ПРОВЕРЬТЕ ТОВАР #', de: 'ARTIKEL PRÜFEN #', ja: '商品番号を確認 #', it: 'VERIFICA ARTICOLO #', pl: 'ZWERYFIKUJ PRZEDMIOT #', uk: 'ПЕРЕВІРТЕ ТОВАР #', tr: 'ÜRÜN NUMARASINI DOĞRULAYIN #', th: 'ตรวจสอบรายการ #', my: 'ပစ္စည်း # ကို စစ်ဆေးပါ', am: 'ዕቃ # ያረጋግጡ' },
  brand: { en: 'Brand:', es: 'Marca:', pt: 'Marca:', zh: '品牌:', vi: 'Thương hiệu:', ko: '브랜드:', ar: 'العلامة التجارية:', fr: 'Marque:', hi: 'ब्रांड:', tl: 'Brand:', ru: 'Бренд:', de: 'Marke:', ja: 'ブランド:', it: 'Marca:', pl: 'Marka:', uk: 'Бренд:', tr: 'Marka:', th: 'แบรนด์:', my: 'အမှတ်တံဆိပ်:', am: 'ብራንድ:' },
  remainingPending: { en: (n) => `⚠️ ${n} other item(s) from this order will be delivered separately`, es: (n) => `⚠️ ${n} otro(s) artículo(s) se entregarán por separado`, pt: (n) => `⚠️ ${n} outro(s) item(ns) será(ão) entregue(s) separadamente`, zh: (n) => `⚠️ 此订单还有 ${n} 件物品将单独配送`, vi: (n) => `⚠️ ${n} mặt hàng khác sẽ được giao riêng`, ko: (n) => `⚠️ 다른 ${n}개 품목이 별도로 배송됩니다`, ar: (n) => `⚠️ سيتم توصيل ${n} عنصر أخرى بشكل منفصل`, fr: (n) => `⚠️ ${n} autre(s) article(s) sera(ont) livré(s) séparément`, hi: (n) => `⚠️ ${n} अन्य आइटम अलग से डिलीवर किए जाएंगे`, tl: (n) => `⚠️ ${n} iba pang item ang ihahatid nang hiwalay`, ru: (n) => `⚠️ ${n} другой(их) товар(ов) будет доставлен(ы) отдельно`, de: (n) => `⚠️ ${n} weitere(r) Artikel wird/werden separat geliefert`, ja: (n) => `⚠️ 他の${n}件の商品は別途配送されます`, it: (n) => `⚠️ ${n} altro/i articolo/i sarà/saranno consegnato/i separatamente`, pl: (n) => `⚠️ ${n} inny(ch) przedmiot(ów) zostanie dostarczony(ch) osobno`, uk: (n) => `⚠️ ${n} інший(х) товар(ів) буде доставлено окремо`, tr: (n) => `⚠️ ${n} diğer ürün ayrı teslim edilecektir`, th: (n) => `⚠️ อีก ${n} รายการจะถูกจัดส่งแยก`, my: (n) => `⚠️ အခြား ${n} ခုကို သီးခြား ပို့ဆောင်ပါမည်`, am: (n) => `⚠️ ${n} ሌላ(ሎች) ዕቃ(ዎች) ለብቻ ይደርሳሉ` },
  remainingDelivered: { en: (n) => `✅ ${n} other item(s) from this order already delivered`, es: (n) => `✅ ${n} otro(s) artículo(s) ya entregado(s)`, pt: (n) => `✅ ${n} outro(s) item(ns) já entregue(s)`, zh: (n) => `✅ 另外 ${n} 件物品已送达`, vi: (n) => `✅ ${n} mặt hàng khác đã được giao`, ko: (n) => `✅ 다른 ${n}개 품목이 이미 배송되었습니다`, ar: (n) => `✅ تم توصيل ${n} عنصر أخرى بالفعل`, fr: (n) => `✅ ${n} autre(s) article(s) déjà livré(s)`, hi: (n) => `✅ ${n} अन्य आइटम पहले ही डिलीवर हो चुके हैं`, tl: (n) => `✅ ${n} iba pang item ay naihatid na`, ru: (n) => `✅ ${n} другой(их) товар(ов) уже доставлен(ы)`, de: (n) => `✅ ${n} weitere(r) Artikel bereits geliefert`, ja: (n) => `✅ 他の${n}件の商品は配送済みです`, it: (n) => `✅ ${n} altro/i articolo/i già consegnato/i`, pl: (n) => `✅ ${n} inny(ch) przedmiot(ów) już dostarczony(ch)`, uk: (n) => `✅ ${n} інший(х) товар(ів) вже доставлено`, tr: (n) => `✅ ${n} diğer ürün zaten teslim edildi`, th: (n) => `✅ อีก ${n} รายการจัดส่งแล้ว`, my: (n) => `✅ အခြား ${n} ခု ပို့ဆောင်ပြီးဖြစ်သည်`, am: (n) => `✅ ${n} ሌላ(ሎች) ዕቃ(ዎች) አስቀድመው ደርሰዋል` },
  specialDeliveryInstructions: { en: 'Special Delivery Instructions', es: 'Instrucciones especiales de entrega', pt: 'Instruções especiais de entrega', zh: '特殊配送说明', vi: 'Hướng dẫn giao hàng đặc biệt', ko: '특별 배송 지침', ar: 'تعليمات توصيل خاصة', fr: 'Instructions de livraison spéciales', hi: 'विशेष डिलीवरी निर्देश', tl: 'Espesyal na tagubilin sa paghahatid', ru: 'Особые инструкции по доставке', de: 'Besondere Lieferanweisungen', ja: '特別配送指示', it: 'Istruzioni di consegna speciali', pl: 'Specjalne instrukcje dostawy', uk: 'Спеціальні інструкції з доставки', tr: 'Özel teslimat talimatları', th: 'คำแนะนำการจัดส่งพิเศษ', my: 'အထူးပို့ဆောင်ရေး ညွှန်ကြားချက်များ', am: 'ልዩ የማድረስ መመሪያዎች' },
  servicesRequired: { en: 'Services Required', es: 'Servicios requeridos', pt: 'Serviços necessários', zh: '所需服务', vi: 'Dịch vụ cần thiết', ko: '필요 서비스', ar: 'الخدمات المطلوبة', fr: 'Services requis', hi: 'आवश्यक सेवाएं', tl: 'Mga serbisyong kinakailangan', ru: 'Необходимые услуги', de: 'Erforderliche Dienste', ja: '必要なサービス', it: 'Servizi richiesti', pl: 'Wymagane usługi', uk: 'Необхідні послуги', tr: 'Gerekli hizmetler', th: 'บริการที่ต้องการ', my: 'လိုအပ်သောဝန်ဆောင်မှုများ', am: 'የሚያስፈልጉ አገልግሎቶች' },
  setupAssembly: { en: 'Setup / Assembly Required', es: 'Montaje requerido', pt: 'Montagem necessária', zh: '需要安装/组装', vi: 'Cần lắp đặt', ko: '설치 / 조립 필요', ar: 'يتطلب التركيب', fr: 'Installation requise', hi: 'सेटअप आवश्यक', tl: 'Kailangan ng setup', ru: 'Требуется сборка', de: 'Aufbau erforderlich', ja: 'セットアップ必要', it: 'Montaggio richiesto', pl: 'Montaż wymagany', uk: 'Потрібна збірка', tr: 'Kurulum gerekli', th: 'ต้องติดตั้ง', my: 'တပ်ဆင်ခြင်းလိုအပ်သည်', am: 'ማዘጋጀት ያስፈልጋል' },
  wastePickup: { en: 'Waste Pickup / Removal', es: 'Retiro de residuos', pt: 'Remoção de resíduos', zh: '废料回收', vi: 'Thu gom rác', ko: '폐기물 수거', ar: 'إزالة النفايات', fr: 'Enlèvement des déchets', hi: 'कचरा संग्रहण', tl: 'Pagkolekta ng basura', ru: 'Вывоз мусора', de: 'Abfallabholung', ja: '廃棄物回収', it: 'Ritiro rifiuti', pl: 'Odbiór odpadów', uk: 'Вивіз сміття', tr: 'Atık toplama', th: 'เก็บขยะ', my: 'အမှိုက်ကောက်ခြင်း', am: 'ቆሻሻ ማንሳት' },
  navigate: { en: 'Navigate', es: 'Navegar', pt: 'Navegar', zh: '导航', vi: 'Dẫn đường', ko: '내비게이션', ar: 'التنقل', fr: 'Naviguer', hi: 'नेविगेट करें', tl: 'Mag-navigate', ru: 'Навигация', de: 'Navigieren', ja: 'ナビ', it: 'Naviga', pl: 'Nawiguj', uk: 'Навігація', tr: 'Yol tarifi', th: 'นำทาง', my: 'လမ်းညွှန်', am: 'አቅጣጫ' },
  call: { en: 'Call', es: 'Llamar', pt: 'Ligar', zh: '拨打', vi: 'Gọi', ko: '전화', ar: 'اتصل', fr: 'Appeler', hi: 'कॉल करें', tl: 'Tawagan', ru: 'Позвонить', de: 'Anrufen', ja: '電話', it: 'Chiama', pl: 'Zadzwoń', uk: 'Зателефонувати', tr: 'Ara', th: 'โทร', my: 'ဖုန်းခေါ်ရန်', am: 'ደውል' },
  completeDelivery: { en: (n) => `Complete Delivery (${n} item${n !== 1 ? 's' : ''})`, es: (n) => `Completar entrega (${n} artículo${n !== 1 ? 's' : ''})`, pt: (n) => `Concluir entrega (${n} ite${n !== 1 ? 'ns' : 'm'})`, zh: (n) => `完成配送 (${n} 件)`, vi: (n) => `Hoàn tất giao hàng (${n})`, ko: (n) => `배송 완료 (${n}개)`, ar: (n) => `إتمام التوصيل (${n})`, fr: (n) => `Terminer la livraison (${n})`, hi: (n) => `डिलीवरी पूरी करें (${n})`, tl: (n) => `Kumpletuhin (${n} item)`, ru: (n) => `Завершить доставку (${n})`, de: (n) => `Lieferung abschließen (${n})`, ja: (n) => `配送完了 (${n}件)`, it: (n) => `Completa consegna (${n})`, pl: (n) => `Zakończ dostawę (${n})`, uk: (n) => `Завершити доставку (${n})`, tr: (n) => `Teslimatı tamamla (${n})`, th: (n) => `จัดส่งเสร็จสิ้น (${n})`, my: (n) => `ပို့ဆောင်မှုပြီးဆုံး (${n})`, am: (n) => `ማድረስ ጨርስ (${n})` },
  deliveredAt: { en: 'Delivered at', es: 'Entregado a las', pt: 'Entregue às', zh: '送达时间', vi: 'Đã giao lúc', ko: '배송 완료', ar: 'تم التسليم في', fr: 'Livré à', hi: 'डिलीवरी हुई', tl: 'Naihatid ng', ru: 'Доставлено в', de: 'Geliefert um', ja: '配送完了', it: 'Consegnato alle', pl: 'Dostarczono o', uk: 'Доставлено о', tr: 'Teslim edildi', th: 'จัดส่งเมื่อ', my: 'ပို့ဆောင်ပြီး', am: 'የደረሰበት ሰዓት' },
  captureSignature: { en: 'Capture Signature', es: 'Capturar firma', pt: 'Capturar assinatura', zh: '获取签名', vi: 'Lấy chữ ký', ko: '서명 캡처', ar: 'التقاط التوقيع', fr: 'Capturer la signature', hi: 'हस्ताक्षर कैप्चर करें', tl: 'Kunan ang lagda', ru: 'Получить подпись', de: 'Unterschrift erfassen', ja: '署名を取得', it: 'Cattura firma', pl: 'Pobierz podpis', uk: 'Отримати підпис', tr: 'İmza al', th: 'จับภาพลายเซ็น', my: 'လက်မှတ်ရယူပါ', am: 'ፊርማ ያንሱ' },
  partialDeliveryItems: { en: (n) => `Partial delivery: ${n} item(s)`, es: (n) => `Entrega parcial: ${n} artículo(s)`, pt: (n) => `Entrega parcial: ${n} item(ns)`, zh: (n) => `部分配送: ${n} 件`, vi: (n) => `Giao một phần: ${n}`, ko: (n) => `부분 배송: ${n}개`, ar: (n) => `توصيل جزئي: ${n}`, fr: (n) => `Livraison partielle: ${n}`, hi: (n) => `आंशिक डिलीवरी: ${n}`, tl: (n) => `Bahagyang paghahatid: ${n}`, ru: (n) => `Частичная доставка: ${n}`, de: (n) => `Teillieferung: ${n}`, ja: (n) => `一部配送: ${n}件`, it: (n) => `Consegna parziale: ${n}`, pl: (n) => `Dostawa częściowa: ${n}`, uk: (n) => `Часткова доставка: ${n}`, tr: (n) => `Kısmi teslimat: ${n}`, th: (n) => `จัดส่งบางส่วน: ${n}`, my: (n) => `တစ်စိတ်တစ်ပိုင်း: ${n}`, am: (n) => `ከፊል ማድረስ: ${n}` },
  clearSignature: { en: 'Clear Signature', es: 'Borrar firma', pt: 'Limpar assinatura', zh: '清除签名', vi: 'Xóa chữ ký', ko: '서명 지우기', ar: 'مسح التوقيع', fr: 'Effacer la signature', hi: 'हस्ताक्षर साफ़ करें', tl: 'I-clear ang lagda', ru: 'Очистить подпись', de: 'Unterschrift löschen', ja: '署名をクリア', it: 'Cancella firma', pl: 'Wyczyść podpis', uk: 'Очистити підпис', tr: 'İmzayı temizle', th: 'ล้างลายเซ็น', my: 'လက်မှတ်ရှင်းပါ', am: 'ፊርማ ያጥፉ' },
  confirmDelivery: { en: 'Confirm Delivery', es: 'Confirmar entrega', pt: 'Confirmar entrega', zh: '确认配送', vi: 'Xác nhận giao hàng', ko: '배송 확인', ar: 'تأكيد التوصيل', fr: 'Confirmer la livraison', hi: 'डिलीवरी की पुष्टि करें', tl: 'Kumpirmahin ang paghahatid', ru: 'Подтвердить доставку', de: 'Lieferung bestätigen', ja: '配送を確認', it: 'Conferma consegna', pl: 'Potwierdź dostawę', uk: 'Підтвердити доставку', tr: 'Teslimatı onayla', th: 'ยืนยันการจัดส่ง', my: 'ပို့ဆောင်မှုအတည်ပြုပါ', am: 'ማድረስ ያረጋግጡ' },
  processing: { en: 'Processing...', es: 'Procesando...', pt: 'Processando...', zh: '处理中...', vi: 'Đang xử lý...', ko: '처리 중...', ar: 'جارٍ المعالجة...', fr: 'Traitement en cours...', hi: 'प्रोसेसिंग...', tl: 'Pinoproseso...', ru: 'Обработка...', de: 'Wird verarbeitet...', ja: '処理中...', it: 'Elaborazione...', pl: 'Przetwarzanie...', uk: 'Обробка...', tr: 'İşleniyor...', th: 'กำลังดำเนินการ...', my: 'လုပ်ဆောင်နေသည်...', am: 'በማስኬድ ላይ...' },
  poweredBy: { en: 'Powered by', es: 'Desarrollado por', pt: 'Desenvolvido por', zh: '技术支持', vi: 'Được cung cấp bởi', ko: '제공:', ar: 'مدعوم من', fr: 'Propulsé par', hi: 'द्वारा संचालित', tl: 'Pinapagana ng', ru: 'Работает на', de: 'Bereitgestellt von', ja: '提供:', it: 'Powered by', pl: 'Obsługiwane przez', uk: 'Працює на', tr: 'Tarafından desteklenmektedir', th: 'ขับเคลื่อนโดย', my: 'ပံ့ပိုးသူ', am: 'አቅራቢ' },
  routeComplete: { en: 'Route Complete!', es: '¡Ruta completada!', pt: 'Rota concluída!', zh: '路线完成！', vi: 'Hoàn tất!', ko: '경로 완료!', ar: 'اكتمل المسار!', fr: 'Itinéraire terminé!', hi: 'रूट पूरा!', tl: 'Ruta kumpleto!', ru: 'Маршрут завершён!', de: 'Route abgeschlossen!', ja: 'ルート完了！', it: 'Percorso completato!', pl: 'Trasa ukończona!', uk: 'Маршрут завершено!', tr: 'Rota tamamlandı!', th: 'เสร็จสมบูรณ์!', my: 'ပြီးပါပြီ!', am: 'ተጠናቅቋል!' },
  allDeliveriesComplete: { en: 'All deliveries complete!', es: '¡Todas las entregas completadas!', pt: 'Todas as entregas concluídas!', zh: '所有配送已完成！', vi: 'Tất cả giao hàng hoàn tất!', ko: '모든 배송 완료!', ar: 'اكتملت جميع التسليمات!', fr: 'Toutes les livraisons terminées!', hi: 'सभी डिलीवरी पूरी!', tl: 'Lahat tapos na!', ru: 'Все доставки завершены!', de: 'Alle Lieferungen abgeschlossen!', ja: '全配送完了！', it: 'Tutte le consegne completate!', pl: 'Wszystkie dostawy ukończone!', uk: 'Усі доставки виконано!', tr: 'Tüm teslimatlar tamamlandı!', th: 'จัดส่งทั้งหมดเสร็จ!', my: 'အားလုံးပြီးပါပြီ!', am: 'ሁሉም ተጠናቅቀዋል!' },
  itemsDelivered: { en: 'Items Delivered', es: 'Artículos entregados', pt: 'Itens entregues', zh: '物品已送达', vi: 'Đã giao', ko: '배송 완료', ar: 'تم التوصيل', fr: 'Articles livrés', hi: 'आइटम डिलीवर हुए', tl: 'Naihatid na', ru: 'Товары доставлены', de: 'Artikel geliefert', ja: '配送済み', it: 'Consegnati', pl: 'Dostarczone', uk: 'Доставлено', tr: 'Teslim edildi', th: 'จัดส่งแล้ว', my: 'ပို့ဆောင်ပြီး', am: 'ደርሰዋል' },
  itemsDeliveredMsg: { en: (n) => `${n} item(s) delivered! Remaining items will be delivered in a future route.`, es: (n) => `¡${n} artículo(s) entregado(s)! Los restantes se entregarán después.`, pt: (n) => `${n} item(ns) entregue(s)! Os restantes serão entregues depois.`, zh: (n) => `${n} 件已送达！剩余物品将在未来配送。`, vi: (n) => `${n} mặt hàng đã giao! Còn lại sẽ giao sau.`, ko: (n) => `${n}개 배송 완료! 나머지는 추후 배송됩니다.`, ar: (n) => `تم توصيل ${n}! سيتم توصيل المتبقي لاحقاً.`, fr: (n) => `${n} article(s) livré(s)! Le reste sera livré ultérieurement.`, hi: (n) => `${n} आइटम डिलीवर! बाकी बाद में डिलीवर होंगे।`, tl: (n) => `${n} item naihatid! Ang natitira ay ihahatid sa susunod.`, ru: (n) => `${n} доставлено! Остальное будет доставлено позже.`, de: (n) => `${n} Artikel geliefert! Rest wird später geliefert.`, ja: (n) => `${n}件配送完了！残りは次回配送されます。`, it: (n) => `${n} consegnato/i! I restanti saranno consegnati dopo.`, pl: (n) => `${n} dostarczono! Reszta zostanie dostarczona później.`, uk: (n) => `${n} доставлено! Решта буде доставлена пізніше.`, tr: (n) => `${n} teslim edildi! Kalan daha sonra teslim edilecek.`, th: (n) => `${n} รายการจัดส่งแล้ว! ที่เหลือจะจัดส่งภายหลัง`, my: (n) => `${n} ခု ပို့ပြီး! ကျန်တာ နောက်မှ ပို့ပါမည်။`, am: (n) => `${n} ደርሰዋል! ቀሪዎቹ በኋላ ይደርሳሉ።` },
  errorTitle: { en: 'Error', es: 'Error', pt: 'Erro', zh: '错误', vi: 'Lỗi', ko: '오류', ar: 'خطأ', fr: 'Erreur', hi: 'त्रुटि', tl: 'Error', ru: 'Ошибка', de: 'Fehler', ja: 'エラー', it: 'Errore', pl: 'Błąd', uk: 'Помилка', tr: 'Hata', th: 'ข้อผิดพลาด', my: 'အမှား', am: 'ስህተት' },
  failedToComplete: { en: 'Failed to complete delivery. Please try again.', es: 'No se pudo completar la entrega. Inténtalo de nuevo.', pt: 'Falha ao concluir entrega. Tente novamente.', zh: '完成配送失败。请重试。', vi: 'Không thể hoàn tất. Vui lòng thử lại.', ko: '배송 완료 실패. 다시 시도해 주세요.', ar: 'فشل في إكمال التوصيل. يرجى المحاولة مرة أخرى.', fr: 'Échec de la livraison. Veuillez réessayer.', hi: 'डिलीवरी विफल। कृपया पुनः प्रयास करें।', tl: 'Hindi nakumpleto. Pakisubukan muli.', ru: 'Не удалось завершить доставку. Попробуйте снова.', de: 'Lieferung fehlgeschlagen. Bitte erneut versuchen.', ja: '配送完了できませんでした。もう一度お試しください。', it: 'Consegna non riuscita. Riprova.', pl: 'Nie udało się. Spróbuj ponownie.', uk: 'Не вдалося завершити. Спробуйте ще раз.', tr: 'Teslimat tamamlanamadı. Tekrar deneyin.', th: 'จัดส่งไม่สำเร็จ กรุณาลองอีกครั้ง', my: 'မအောင်မြင်ပါ။ ထပ်ကြိုးစားပါ။', am: 'ማጠናቀቅ አልተቻለም። እንደገና ይሞክሩ።' },
};

const t = (key, lang = 'en') => {
  const entry = driverTranslations[key];
  if (!entry) return key;
  return entry[lang] || entry['en'];
};
// =====================================================

const DriverView = () => {
  const { routeId } = useParams();
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStop, setExpandedStop] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [routeStarted, setRouteStarted] = useState(false);
  const [optimizingRoute, setOptimizingRoute] = useState(false);
  
  // GPS Tracking state
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const watchIdRef = useRef(null);
  const updateIntervalRef = useRef(null);
  const locationRef = useRef(null);

  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Toast notification state
  const [notifications, setNotifications] = useState([]);
  
  // Add notification helper
  const addNotification = (notification) => {
    const id = Date.now();
    const newNotification = { ...notification, id, timestamp: new Date() };
    setNotifications(prev => [newNotification, ...prev].slice(0, 5));
    
    // Auto-remove after 4 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  };

  // Load route data
  useEffect(() => {
    loadRoute();
    
    return () => {
      stopLocationTracking();
    };
  }, [routeId]);

  // Real-time subscription for route and stop updates
  // Safe now because location updates go to driver_locations table, not delivery_routes
  useEffect(() => {
    if (!routeId) return;

    console.log('🔌 Setting up real-time subscriptions for DriverView...');
    
    const channel = supabase
      .channel(`driver-view-${routeId}`)
      // Listen for route changes (status changes, dispatch, etc.)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'delivery_routes', filter: `id=eq.${routeId}` },
        (payload) => {
          console.log('🔄 Route updated:', payload.eventType);
          loadRoute();
        }
      )
      // Listen for stop changes (order changes, status updates)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'delivery_route_stops', filter: `route_id=eq.${routeId}` },
        (payload) => {
          console.log('🔄 Route stops updated:', payload.eventType);
          loadRoute();
        }
      )
      .subscribe((status) => {
        console.log('📡 DriverView subscription status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up DriverView subscriptions');
      supabase.removeChannel(channel);
    };
  }, [routeId]);

  const loadRoute = async () => {
    try {
      setLoading(true);
      console.log('📦 Loading route:', routeId);
      
      // One call, server side, for everything on this route. The driver is
      // not logged in, and since the anon lockdown (sql/anon-lockdown/02) the
      // public key reads zero rows from receipts, receipt_items, item_statuses
      // and inventory_units. /api/driver-route gathers them with the service
      // key, scoped to this route's stops; the route UUID in the link is the
      // credential, as it already is for /api/complete-delivery.
      const bundleRes = await fetch(`/api/driver-route?routeId=${encodeURIComponent(routeId)}`);
      const bundle = await bundleRes.json().catch(() => ({}));
      if (!bundleRes.ok || !bundle?.success || !bundle.route) {
        const routeError = new Error(bundle?.error || `Route request failed (${bundleRes.status})`);
        console.error('❌ Route error:', routeError);
        throw routeError;
      }
      const routeData = bundle.route;
      const stopsData = bundle.stops || [];
      const receiptsById = Object.fromEntries((bundle.receipts || []).map(r => [String(r.id), r]));
      const itemsByReceipt = {};
      for (const it of (bundle.items || [])) {
        const k = String(it.receipt_id);
        (itemsByReceipt[k] = itemsByReceipt[k] || []).push(it);
      }
      const statusesByReceipt = {};
      for (const st of (bundle.item_statuses || [])) {
        const k = String(st.receipt_id);
        (statusesByReceipt[k] = statusesByReceipt[k] || []).push(st);
      }
      const allUnits = bundle.inventory_units || [];
      const prefsByReceipt = Object.fromEntries((bundle.customer_preferences || []).map(p => [String(p.receipt_id), p]));

      console.log('✅ Route loaded:', routeData);
      
      // Sort stops by stop_order/stop_number, with fallback to route's stop_order array
      let sortedStops = [...(stopsData || [])];
      
      // If route has a stop_order array, use it as the primary ordering
      if (routeData.stop_order && Array.isArray(routeData.stop_order) && routeData.stop_order.length > 0) {
        console.log('📊 Using route stop_order array for sorting:', routeData.stop_order);
        sortedStops.sort((a, b) => {
          const indexA = routeData.stop_order.indexOf(a.receipt_id);
          const indexB = routeData.stop_order.indexOf(b.receipt_id);
          // If not found in stop_order array, fall back to stop_number
          if (indexA === -1 && indexB === -1) {
            return (a.stop_order || a.stop_number || 0) - (b.stop_order || b.stop_number || 0);
          }
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } else {
        // Fallback to stop_order/stop_number fields
        sortedStops.sort((a, b) => {
          return (a.stop_order || a.stop_number || 0) - (b.stop_order || b.stop_number || 0);
        });
      }
      
      console.log('📊 Sorted stops order:', sortedStops.map((s, i) => ({ 
        index: i + 1, 
        receipt_id: s.receipt_id, 
        stop_order: s.stop_order, 
        stop_number: s.stop_number 
      })));
      
      console.log('📍 Stops loaded:', sortedStops);

      // For each stop, fetch the receipt and FILTER items based on inventory_unit_ids
      const stopsWithReceipts = await Promise.all(
        (sortedStops || []).map(async (stop) => {
          console.log('🔍 Processing stop:', stop.id, 'inventory_unit_ids:', stop.inventory_unit_ids);
          
          if (stop.receipt_id) {
            try {
              // The receipt and its lines come from the route bundle above.
              const receiptData = receiptsById[String(stop.receipt_id)];
              if (!receiptData) {
                console.error('❌ Receipt missing from route bundle:', stop.receipt_id);
                return stop;
              }
              const itemsData = itemsByReceipt[String(stop.receipt_id)] || [];
              let allItems = itemsData.length > 0 ? itemsData : (receiptData?.items || []);

              // =====================================================
              // PARTIAL DELIVERY LOGIC - Filter items based on inventory_unit_ids
              // =====================================================
              let deliveryItems = [];
              let inventoryUnitsForStop = [];
              const hasSpecificUnits = Array.isArray(stop.inventory_unit_ids) && stop.inventory_unit_ids.length > 0;
              // Unitless items (no inventory unit) ride the stop by receipt_item_id.
              const unitlessItemIds = Array.isArray(stop.receipt_item_ids) ? stop.receipt_item_ids.map(String) : [];
              const hasUnitlessItems = unitlessItemIds.length > 0;
              // A stop is "specific" (a partial delivery) if it carries EITHER
              // specific units or specific unitless items.
              const hasSpecificItems = hasSpecificUnits || hasUnitlessItems;
              
              console.log('🔎 Specific units:', hasSpecificUnits, stop.inventory_unit_ids, '| unitless items:', hasUnitlessItems, unitlessItemIds);
              
              if (!hasSpecificItems) {
                // No specific tracking at all - FULL delivery or legacy receipt.
                // Show all items from the receipt.
                deliveryItems = allItems;
                console.log('📦 No specific items - showing ALL receipt items:', allItems.length);
              } else {
                // PARTIAL DELIVERY - Fetch ONLY the specific inventory units for this stop
                // (may be none, if this stop carries only unitless items).
                if (hasSpecificUnits) {
                  const wantedUnitIds = new Set(stop.inventory_unit_ids.map(String));
                  inventoryUnitsForStop = allUnits.filter(u => wantedUnitIds.has(String(u.id)));
                } else {
                  inventoryUnitsForStop = [];
                }
                console.log('📦 Fetched inventory units for stop:', inventoryUnitsForStop.length, inventoryUnitsForStop.map(u => ({ 
                  id: u.id, 
                  sku: u.sku, 
                  unit_serial: u.unit_serial,
                  receipt_item_id: u.receipt_item_id 
                })));
                
                // FIX: Handle MIXED scenarios - some units with receipt_item_id, some without
                // Step 1: Get items matched by receipt_item_id (for units that have it)
                const unitsWithReceiptItemId = inventoryUnitsForStop.filter(u => u.receipt_item_id);
                const unitsWithoutReceiptItemId = inventoryUnitsForStop.filter(u => !u.receipt_item_id);
                
                console.log(`📋 Units with receipt_item_id: ${unitsWithReceiptItemId.length}, without: ${unitsWithoutReceiptItemId.length}`);
                
                const matchedItemIds = new Set();
                
                // Match items by receipt_item_id first (most precise)
                if (unitsWithReceiptItemId.length > 0) {
                  const receiptItemIdsInDelivery = unitsWithReceiptItemId.map(u => u.receipt_item_id);
                  
                  const itemsByReceiptItemId = allItems.filter(item => 
                    receiptItemIdsInDelivery.includes(item.id)
                  ).map(item => {
                    const unitsForItem = inventoryUnitsForStop.filter(u => 
                      String(u.receipt_item_id) === String(item.id)
                    );
                    matchedItemIds.add(item.id);
                    return {
                      ...item,
                      quantity: unitsForItem.length || 1,
                      inventory_units: unitsForItem
                    };
                  });
                  
                  deliveryItems.push(...itemsByReceiptItemId);
                  console.log('✅ Items matched by receipt_item_id:', itemsByReceiptItemId.length);
                }
                
                // Match remaining items by SKU (for units without receipt_item_id)
                if (unitsWithoutReceiptItemId.length > 0) {
                  const skusForUnlinkedUnits = [...new Set(unitsWithoutReceiptItemId.map(u => u.sku?.toLowerCase().trim()))];
                  
                  const itemsBySku = allItems.filter(item => 
                    !matchedItemIds.has(item.id) && // Not already matched by receipt_item_id
                    skusForUnlinkedUnits.includes(item.sku?.toLowerCase().trim())
                  ).map(item => {
                    const skuKey = item.sku?.toLowerCase().trim();
                    const unitsOfSku = unitsWithoutReceiptItemId.filter(u => 
                      u.sku?.toLowerCase().trim() === skuKey
                    );
                    return {
                      ...item,
                      quantity: unitsOfSku.length || 1,
                      inventory_units: unitsOfSku
                    };
                  });
                  
                  deliveryItems.push(...itemsBySku);
                  console.log('✅ Items matched by SKU (for unlinked units):', itemsBySku.length);
                }
                
                // =====================================================
                // UNITLESS ITEMS: items on this stop that have no inventory
                // unit (tracked by receipt_item_id in stop.receipt_item_ids).
                // Add any that aren't already in deliveryItems.
                // =====================================================
                if (hasUnitlessItems) {
                  const alreadyIncluded = new Set(deliveryItems.map(i => String(i.id)));
                  const unitlessItems = allItems
                    .filter(item => unitlessItemIds.includes(String(item.id)) && !alreadyIncluded.has(String(item.id)))
                    .map(item => ({
                      ...item,
                      quantity: item.quantity || 1,
                      inventory_units: [] // no units — status-tracked only
                    }));
                  deliveryItems.push(...unitlessItems);
                  console.log('✅ Unitless items merged into delivery:', unitlessItems.length);
                }
                
                console.log('✅ Total filtered items for partial delivery:', deliveryItems.length, 'of', allItems.length, 'total items');
              }
              
              // =====================================================
              // For partial deliveries, determine status of OTHER items
              // (items NOT on this stop) so we can show accurate messaging
              // =====================================================
              let remainingDeliveredCount = 0;
              let remainingPendingCount = 0;
              
              if (hasSpecificItems && deliveryItems.length < allItems.length) {
                const deliveryItemIds = new Set(deliveryItems.map(i => String(i.id)));
                const remainingItems = allItems.filter(i => !deliveryItemIds.has(String(i.id)));
                const remainingItemIds = remainingItems.map(i => i.id).filter(Boolean);
                
                // Check inventory_units for remaining items
                if (remainingItemIds.length > 0) {
                  try {
                    const remainingIdSet = new Set(remainingItemIds.map(String));
                    const remainingUnits = allUnits.filter(u =>
                      u.receipt_item_id && remainingIdSet.has(String(u.receipt_item_id))
                    );
                    
                    // Also check item_statuses as fallback (from the bundle)
                    const remainingStatuses = statusesByReceipt[String(stop.receipt_id)] || [];
                    
                    const statusMap = new Map(); // receipt_item_id -> 'delivered' | 'pending'
                    
                    // First pass: inventory_units
                    (remainingUnits || []).forEach(u => {
                      if (UNIT_TERMINAL_STATUSES.includes(u.status)) {
                        statusMap.set(String(u.receipt_item_id), 'delivered');
                      }
                    });
                    
                    // Second pass: item_statuses (fallback for items without inventory units)
                    (remainingStatuses || []).forEach(s => {
                      if (!statusMap.has(String(s.receipt_item_id))) {
                        if (ITEM_TERMINAL_STATUSES.includes(s.status)) {
                          statusMap.set(String(s.receipt_item_id), 'delivered');
                        }
                      }
                    });
                    
                    remainingItems.forEach(item => {
                      if (statusMap.get(String(item.id)) === 'delivered') {
                        remainingDeliveredCount++;
                      } else {
                        remainingPendingCount++;
                      }
                    });
                    
                    console.log(`📊 Remaining items: ${remainingDeliveredCount} delivered, ${remainingPendingCount} pending`);
                  } catch (e) {
                    // Fallback: assume all remaining are pending
                    remainingPendingCount = remainingItems.length;
                    console.error('⚠️ Error checking remaining item statuses:', e);
                  }
                }
              }

              // Customer preferences, when the store recorded any (from the bundle)
              const customerPrefs = prefsByReceipt[String(stop.receipt_id)] || null;
              
              const receipt = {
                ...receiptData,
                items: deliveryItems,           // Only items being delivered in THIS stop
                all_items: allItems,             // Keep reference to all items
                customer_preferences: customerPrefs,
                inventory_units: inventoryUnitsForStop,
                remaining_delivered_count: remainingDeliveredCount,
                remaining_pending_count: remainingPendingCount
              };
              
              return { 
                ...stop, 
                receipts: receipt,
                is_partial: hasSpecificItems && deliveryItems.length < allItems.length
              };
            } catch (err) {
              console.error('❌ Error fetching receipt:', err);
              return stop;
            }
          }
          return stop;
        })
      );

      console.log('📦 Final stops with receipts:', stopsWithReceipts);

      setRoute(routeData);
      setStops(stopsWithReceipts);
      
      // Auto-start tracking if route is actually in progress
      if (routeData.status === 'in_progress') {
        setRouteStarted(true);
        startLocationTracking();
      }
    } catch (err) {
      console.error('Error loading route:', err);
      setError('Failed to load route. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // GPS Location Tracking
  const startLocationTracking = async () => {
    console.log('🛰️ startLocationTracking called');
    
    if (!navigator.geolocation) {
      console.error('❌ Geolocation not supported');
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    try {
      setTrackingEnabled(true);
      setLocationError(null);
      
      // Mobile-friendly options: allow cached position initially, longer timeout
      const initialOptions = {
        enableHighAccuracy: false, // Start with low accuracy for faster initial fix
        timeout: 20000,            // 20 second timeout for mobile
        maximumAge: 60000          // Accept 1-minute old cached position initially
      };
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy, heading, speed } = position.coords;
          const location = { latitude, longitude, accuracy, heading, speed };
          
          console.log('📍 Initial location SUCCESS:', location);
          setCurrentLocation(location);
          locationRef.current = location;
          
          sendLocationToServer(location);
          
          // Start watching position with high accuracy now that we have initial fix
          watchIdRef.current = navigator.geolocation.watchPosition(
            (pos) => {
              const loc = {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
                heading: pos.coords.heading,
                speed: pos.coords.speed
              };
              setCurrentLocation(loc);
              locationRef.current = loc;
            },
            (err) => {
              console.error('Watch position error:', err);
              // Don't disable tracking on watch errors - we still have last known position
            },
            {
              enableHighAccuracy: true,
              maximumAge: 10000,  // Accept 10-second old positions while moving
              timeout: 30000     // 30 second timeout for continuous watching
            }
          );
          
          // Send location every 30 seconds
          updateIntervalRef.current = setInterval(() => {
            if (locationRef.current) {
              sendLocationToServer(locationRef.current);
            }
          }, 30000);
        },
        (error) => {
          console.error('❌ Geolocation error:', error);
          // More user-friendly error messages
          let errorMsg = 'Unable to get location';
          if (error.code === 1) {
            errorMsg = 'Location permission denied. Please enable location access.';
          } else if (error.code === 2) {
            errorMsg = 'Location unavailable. Please check GPS settings.';
          } else if (error.code === 3) {
            errorMsg = 'Location request timed out. Retrying...';
            // Auto-retry once on timeout with more lenient settings
            setTimeout(() => {
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  const loc = {
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    accuracy: pos.coords.accuracy
                  };
                  setCurrentLocation(loc);
                  locationRef.current = loc;
                  setLocationError(null);
                  sendLocationToServer(loc);
                },
                () => setLocationError('Location still unavailable. Continue without tracking.'),
                { enableHighAccuracy: false, timeout: 30000, maximumAge: 300000 }
              );
            }, 1000);
            return; // Don't disable tracking yet
          }
          setLocationError(errorMsg);
          setTrackingEnabled(false);
        },
        initialOptions
      );
    } catch (err) {
      console.error('Error starting location tracking:', err);
      setLocationError('Failed to start location tracking');
      setTrackingEnabled(false);
    }
  };

  const stopLocationTracking = () => {
    console.log('🛑 Stopping location tracking');
    
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
    
    setTrackingEnabled(false);
  };

  // Send location to server - use driver_locations table
  const sendLocationToServer = async (location) => {
    if (!routeId || !location) return;
    
    try {
      console.log('📤 Sending location to driver_locations table:', location);
      
      // Through the server: the public key may insert a driver_locations
      // row but cannot update one, so a direct upsert fails from the second
      // ping on. /api/update-driver-location upserts with the service key.
      const res = await fetch('/api/update-driver-location', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          routeId,
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          heading: location.heading,
          speed: location.speed
        })
      });

      if (!res.ok) {
        console.error('❌ Error updating driver location:', res.status, await res.text().catch(() => ''));
      } else {
        console.log('✅ Driver location updated successfully');
      }
    } catch (err) {
      console.error('Error sending location:', err);
    }
  };

  // Start the route
  const startRoute = async () => {
    console.log('🚀 Starting route...');
    setOptimizingRoute(true);
    
    try {
      // First, start location tracking
      await startLocationTracking();
      
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      // Then optimize the route order based on current traffic
      console.log('🗺️ Optimizing route with Google Routes API...');
      
      // Call the route optimization endpoint
      const response = await fetch('/api/optimize-route', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(session && { 'Authorization': `Bearer ${session.access_token}` })
        },
        body: JSON.stringify({
          routeId,
          currentLocation: locationRef.current
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Route optimized:', result);
        
        // Reload route to get new stop order
        await loadRoute();
      } else {
        console.warn('⚠️ Route optimization failed, using existing order');
      }
      
      // Update route status (server side: the public key has no update
      // rule on delivery_routes)
      const startRes = await fetch('/api/driver-route', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ routeId, action: 'start' })
      });
      const startResult = await startRes.json().catch(() => ({}));
      if (!startRes.ok || !startResult.success) {
        throw new Error(startResult.error || `Start failed (${startRes.status})`);
      }
      
      setRouteStarted(true);
      setRoute(prev => ({ ...prev, status: 'in_progress' }));
      
    } catch (err) {
      console.error('Error starting route:', err);
      addNotification({ title: 'Error', message: 'Failed to start route. Please try again.', isEscalation: true });
    } finally {
      setOptimizingRoute(false);
    }
  };

  // Navigation
  const handleNavigateToStop = (stop) => {
    const address = stop.customer_address || stop.receipts?.delivery_address || stop.receipts?.customer_address;
    if (!address) {
      addNotification({ title: 'No Address', message: 'No address available for this stop', isEscalation: true });
      return;
    }
    
    // Open in Google Maps or Apple Maps based on device
    const encodedAddress = encodeURIComponent(address);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      window.open(`maps://maps.apple.com/?daddr=${encodedAddress}&dirflg=d`, '_blank');
    } else {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`, '_blank');
    }
  };

  // Call customer
  const handleCallCustomer = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  // Complete delivery
  const handleCompleteDelivery = (stop) => {
    setSelectedStop(stop);
    setShowSignatureModal(true);
    setHasSignature(false);
    
    // Initialize canvas
    setTimeout(() => {
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }, 100);
  };

  // Signature drawing
  const getCoordinates = (e) => {
    if (e.touches) {
      const touch = e.touches[0];
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    }
    return {
      x: e.nativeEvent.offsetX,
      y: e.nativeEvent.offsetY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
  };

  // Submit delivery
  const submitDelivery = async () => {
    if (!selectedStop || !hasSignature) return;
    
    setCompleting(true);
    
    try {
      const signatureData = canvasRef.current.toDataURL('image/png');
      const inventoryUnitIds = selectedStop.inventory_unit_ids || [];
      const unitlessReceiptItemIds = selectedStop.receipt_item_ids || [];
      // Sent to the API: "this stop carries a specific item list" (units or
      // unitless ids). The API treats it as a hint and lets remaining-item
      // counting decide the receipt's final status, so keeping it broad is
      // safe even for stops created before is_partial_delivery was set.
      const isPartialDelivery = selectedStop.is_partial_delivery || (inventoryUnitIds.length > 0) || (unitlessReceiptItemIds.length > 0);
      // Driver-facing messaging: only call it a partial if dispatch actually
      // marked it one. Post-inventory EVERY stop carries receipt_item_ids,
      // so "has ids" no longer implies partial.
      const isPartialStop = selectedStop.is_partial_delivery === true;
      const deliveredItemCount = inventoryUnitIds.length + unitlessReceiptItemIds.length;
      
      console.log('📦 Completing delivery:', {
        stopId: selectedStop.id,
        receiptId: selectedStop.receipt_id,
        isPartial: isPartialDelivery,
        inventoryUnitIds: inventoryUnitIds.length,
        unitlessReceiptItemIds: unitlessReceiptItemIds.length
      });
      
      // Call the complete-delivery endpoint
      const response = await fetch('/api/complete-delivery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stopId: selectedStop.id,
          routeId,
          receiptId: selectedStop.receipt_id,
          signatureData,
          inventoryUnitIds: inventoryUnitIds,
          unitlessReceiptItemIds: unitlessReceiptItemIds,
          isPartial: isPartialDelivery
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Delivery completed:', result);
        
        // Update local state
        const updatedStops = stops.map(s => 
          s.id === selectedStop.id 
            ? { ...s, status: 'delivered', completed_at: new Date().toISOString() }
            : s
        );
        setStops(updatedStops);
        
        setShowSignatureModal(false);
        setSelectedStop(null);

        const remainingStops = updatedStops.filter(s => s.status !== 'delivered');

        if (remainingStops.length === 0) {
          console.log('🎉 All stops delivered');
          
          // Server side: the endpoint re-checks that every stop is delivered
          const doneRes = await fetch('/api/driver-route', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ routeId, action: 'complete' })
          });
          if (!doneRes.ok) {
            console.error('⚠️ Could not mark route completed:', doneRes.status, await doneRes.text().catch(() => ''));
          }
          
          setRoute(prev => ({ ...prev, status: 'completed' }));
          stopLocationTracking();
          addNotification({ title: t('routeComplete', route?.driver_language || 'en'), message: t('allDeliveriesComplete', route?.driver_language || 'en') });
        } else if (isPartialStop) {
          addNotification({ title: t('itemsDelivered', route?.driver_language || 'en'), message: t('itemsDeliveredMsg', route?.driver_language || 'en')(deliveredItemCount) });
        }
      } else {
        throw new Error(result.error || 'Failed to complete delivery');
      }
    } catch (err) {
      console.error('Error completing delivery:', err);
      addNotification({ title: t('errorTitle', route?.driver_language || 'en'), message: t('failedToComplete', route?.driver_language || 'en'), isEscalation: true });
    } finally {
      setCompleting(false);
    }
  };

  // RENDER - LOADING STATE
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-300 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 shadow-lg flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <p className="text-slate-900 font-medium">Loading route...</p>
          <p className="text-slate-500 text-sm mt-1">Please wait</p>
        </div>
      </div>
    );
  }

  // RENDER - ERROR STATE
  if (error) {
    return (
      <div className="min-h-screen bg-slate-300 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-slate-900 font-medium mb-2">Unable to load route</p>
          <p className="text-slate-500 text-sm mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/20"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  const completedStops = stops.filter(s => s.status === 'delivered').length;
  const totalStops = stops.length;
  const progress = totalStops > 0 ? (completedStops / totalStops) * 100 : 0;
  const lang = route?.driver_language || 'en';

  return (
    <div className="min-h-screen bg-slate-300">
      {/* CSS Animations */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      
      {/* Toast Notifications */}
      <div className="fixed right-4 z-[200] space-y-3 pointer-events-none" style={{ top: 'calc(env(safe-area-inset-top) + 1rem)' }}>
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`pointer-events-auto rounded-2xl p-4 shadow-2xl max-w-sm border ${
              notification.isEscalation 
                ? 'bg-white border-red-200 shadow-red-100'
                : 'bg-white border-emerald-200 shadow-emerald-100'
            }`}
            style={{ animation: 'slideInRight 0.3s ease-out' }}
          >
            <div className="flex items-start space-x-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg ${
                notification.isEscalation
                  ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/30'
                  : 'bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-emerald-500/30'
              }`}>
                {notification.isEscalation ? (
                  <AlertCircle className="w-5 h-5 text-white" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900">
                  {notification.title}
                </p>
                <p className="text-xs mt-0.5 text-slate-500 line-clamp-2">
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  notification.isEscalation
                    ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                    : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                }`}
              >
                {t('dismiss', lang)}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div 
        className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">{route?.route_name || t('deliveryRoute', lang)}</h1>
                <p className="text-sm text-slate-500">
                  {completedStops}/{totalStops} {t('deliveriesComplete', lang)}
                </p>
              </div>
            </div>
            
            {trackingEnabled && (
              <div className="flex flex-col items-end">
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-xs font-medium text-blue-700">{t('live', lang)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Start Route Button */}
      {!routeStarted && (
        <div className="p-4">
          <button
            onClick={startRoute}
            disabled={optimizingRoute}
            className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl flex items-center justify-center gap-3 disabled:opacity-70 shadow-lg shadow-blue-500/20"
          >
            {optimizingRoute ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                {t('optimizingForTraffic', lang)}
              </>
            ) : (
              <>
                <Play className="w-6 h-6" />
                {t('startRoute', lang)}
              </>
            )}
          </button>
          {!optimizingRoute && (
            <p className="text-center text-slate-500 text-xs mt-2">
              {t('routeOptimizedNote', lang)}
            </p>
          )}
        </div>
      )}

      {/* Stops List */}
      <div className="p-4 space-y-4">
        {stops.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-500">{t('noStops', lang)}</p>
          </div>
        ) : (
          stops.map((stop, index) => {
          const isCompleted = stop.status === 'delivered';
          const isExpanded = expandedStop === stop.id;
          const receipt = stop.receipts;
          
          // Get data from receipt
          const customerName = stop.customer_name || receipt?.customer_name || 'Unknown Customer';
          const address = stop.customer_address || receipt?.delivery_address || receipt?.customer_address || 'No address';
          const phone = stop.customer_phone || receipt?.customer_phone;
          const items = receipt?.items || [];
          const deliveryInstructions = receipt?.customer_notes || receipt?.customer_preferences?.notes || receipt?.customer_preferences?.delivery_instructions;
          
          // Delivery services (setup, waste removal)
          const deliveryServices = receipt?.delivery_services || receipt?.deliveryServices;
          const hasSetup = deliveryServices?.setup?.enabled;
          const hasWastePickup = deliveryServices?.wastePickup?.enabled;
          
          // Payment info
          const total = parseFloat(receipt?.total) || 0;
          const amountPaid = parseFloat(receipt?.amount_paid) || 0;
          const balanceDue = Math.max(0, total - amountPaid);
          const paymentStatus = amountPaid >= total ? 'paid' : amountPaid > 0 ? 'partial' : 'unpaid';
          
          // Delivery preferences
          const prefs = receipt?.customer_preferences;
          const preferredDay = prefs?.preferred_day;
          const preferredTime = prefs?.preferred_time;

          return (
            <div
              key={stop.id}
              className={`rounded-xl border transition-all duration-200 shadow-sm ${
                isCompleted
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-white border-slate-200'
              }`}
            >
              {/* Stop Header */}
              <button
                onClick={() => setExpandedStop(isExpanded ? null : stop.id)}
                className="w-full p-4 flex items-center gap-3 text-left"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isCompleted
                    ? 'bg-emerald-100 border border-emerald-300'
                    : 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/20'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <span className="text-white font-bold">{index + 1}</span>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900 truncate">{customerName}</h3>
                    {stop.is_partial && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full border border-amber-200">
                        {t('partial', lang)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 truncate">{address}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-slate-400">
                      {t('itemsToDeliver', lang)(items.length)}
                    </p>
                    {/* Service indicators */}
                    {hasSetup && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded border border-purple-200">
                        <Wrench className="w-3 h-3" />
                        {t('setup', lang)}
                      </span>
                    )}
                    {hasWastePickup && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded border border-orange-200">
                        <Trash2 className="w-3 h-3" />
                        {t('waste', lang)}
                      </span>
                    )}
                  </div>
                </div>
                
                <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-slate-200 pt-4">
                  {/* Customer Contact */}
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{address}</span>
                  </div>
                  {phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${phone}`} className="text-blue-600 font-medium">{phone}</a>
                    </div>
                  )}

                  {/* Preferred Delivery Time */}
                  {(preferredDay || preferredTime) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-blue-700 uppercase mb-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {t('preferredDeliveryTime', lang)}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {preferredDay && (
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm border border-blue-200">
                            {preferredDay}
                          </span>
                        )}
                        {preferredTime && (
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm border border-blue-200">
                            {preferredTime}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Payment Information */}
                  <div className={`rounded-lg p-3 ${
                    paymentStatus === 'paid' 
                      ? 'bg-emerald-50 border border-emerald-200' 
                      : paymentStatus === 'partial'
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <h4 className={`text-xs font-semibold uppercase mb-2 ${
                      paymentStatus === 'paid' ? 'text-emerald-700' : 
                      paymentStatus === 'partial' ? 'text-amber-700' : 'text-red-700'
                    }`}>
                      💰 {t('paymentStatus', lang)}
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">{t('orderTotal', lang)}</span>
                        <span className="text-slate-900 font-medium">${total.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">{t('amountPaid', lang)}</span>
                        <span className="text-emerald-600">${amountPaid.toFixed(2)}</span>
                      </div>
                      {balanceDue > 0 && (
                        <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                          <span className={paymentStatus === 'unpaid' ? 'text-red-700 font-semibold' : 'text-amber-700 font-semibold'}>
                            {paymentStatus === 'unpaid' ? t('collectOnDelivery', lang) : t('balanceDue', lang)}
                          </span>
                          <span className={paymentStatus === 'unpaid' ? 'text-red-700 font-bold text-lg' : 'text-amber-700 font-bold'}>
                            ${balanceDue.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {paymentStatus === 'paid' && (
                        <div className="text-emerald-700 font-semibold text-center mt-1">
                          {t('paidInFull', lang)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Items to Deliver - FILTERED TO ONLY THIS STOP'S ITEMS */}
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2 flex items-center gap-2">
                      <Package className="w-3 h-3" />
                      {t('itemsToDeliverHeader', lang)}
                      {stop.is_partial && (
                        <span className="text-amber-600">{t('partialOrder', lang)}</span>
                      )}
                    </h4>
                    <div className="space-y-3">
                      {items.map((item, i) => (
                        <div key={i} className="bg-white rounded-lg p-3 border border-slate-200">
                          {/* ITEM NUMBER - MOST PROMINENT */}
                          <div className="mb-2 p-2 bg-amber-50 border-2 border-amber-400 rounded-lg">
                            <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide mb-1">
                              ⚠️ {t('verifyItem', lang)}
                            </div>
                            <div className="text-2xl font-bold text-amber-700 font-mono tracking-wider">
                              {item.inventory_units && item.inventory_units.length > 0 
                                ? item.inventory_units.map(u => u.sku || u.unit_serial || item.sku).join(', ')
                                : item.sku}
                            </div>
                            {item.manufacturer?.name && (
                              <div className="mt-1 text-sm font-semibold text-amber-800">
                                {t('brand', lang)} {item.manufacturer.name}
                              </div>
                            )}
                          </div>
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-slate-900 font-medium">
                                {item.quantity || 1}x {item.name || item.item_name || item.sku}
                              </span>
                            </div>
                            <span className="text-slate-400 font-mono text-xs">{item.sku}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Show remaining items info if partial */}
                    {stop.is_partial && receipt?.all_items && (receipt?.remaining_pending_count > 0 || receipt?.remaining_delivered_count > 0) && (
                      <div className="mt-3 pt-3 border-t border-slate-200 text-xs space-y-1">
                        {receipt.remaining_pending_count > 0 && (
                          <div className="text-amber-700">
                            {t('remainingPending', lang)(receipt.remaining_pending_count)}
                          </div>
                        )}
                        {receipt.remaining_delivered_count > 0 && (
                          <div className="text-emerald-600">
                            {t('remainingDelivered', lang)(receipt.remaining_delivered_count)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Special Delivery Instructions */}
                  {deliveryInstructions && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-amber-700 uppercase mb-1 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        {t('specialDeliveryInstructions', lang)}
                      </h4>
                      <p className="text-sm text-amber-800">{deliveryInstructions}</p>
                    </div>
                  )}

                  {/* Services Required (Setup & Waste Removal) */}
                  {(hasSetup || hasWastePickup) && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-purple-700 uppercase mb-2 flex items-center gap-1">
                        <Wrench className="w-3 h-3" />
                        {t('servicesRequired', lang)}
                      </h4>
                      <div className="space-y-2">
                        {hasSetup && (
                          <div className="flex items-center gap-2 bg-purple-100 rounded-lg px-3 py-2 border border-purple-200">
                            <Wrench className="w-4 h-4 text-purple-600" />
                            <div className="flex-1">
                              <span className="text-purple-800 font-medium">{t('setupAssembly', lang)}</span>
                              {deliveryServices?.setup?.price && (
                                <span className="text-purple-600 text-sm ml-2">
                                  (${parseFloat(deliveryServices.setup.price).toFixed(2)})
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {hasWastePickup && (
                          <div className="flex items-center gap-2 bg-orange-100 rounded-lg px-3 py-2 border border-orange-200">
                            <Trash2 className="w-4 h-4 text-orange-600" />
                            <div className="flex-1">
                              <span className="text-orange-800 font-medium">{t('wastePickup', lang)}</span>
                              {deliveryServices?.wastePickup?.price && (
                                <span className="text-orange-600 text-sm ml-2">
                                  (${parseFloat(deliveryServices.wastePickup.price).toFixed(2)})
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {!isCompleted && (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleNavigateToStop(stop)}
                        className="py-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 font-semibold flex items-center justify-center gap-2 text-sm"
                      >
                        <Navigation className="w-4 h-4" />
                        {t('navigate', lang)}
                      </button>
                      {phone && (
                        <button
                          onClick={() => handleCallCustomer(phone)}
                          className="py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 font-semibold flex items-center justify-center gap-2 text-sm"
                        >
                          <Phone className="w-4 h-4" />
                          {t('call', lang)}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Complete Delivery Button */}
                  {!isCompleted && (
                    <button
                      onClick={() => handleCompleteDelivery(stop)}
                      className="w-full py-3 bg-blue-600 text-white font-bold rounded-lg flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {t('completeDelivery', lang)(items.length)}
                    </button>
                  )}

                  {/* Completed Info */}
                  {isCompleted && stop.completed_at && (
                    <p className="text-center text-sm text-emerald-600 font-medium">
                      {t('deliveredAt', lang)} {new Date(stop.completed_at).toLocaleTimeString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })
        )}
      </div>

      {/* Signature Modal */}
      {showSignatureModal && selectedStop && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{t('captureSignature', lang)}</h3>
                <p className="text-sm text-slate-500">
                  {selectedStop.customer_name || selectedStop.receipts?.customer_name}
                </p>
                {selectedStop.is_partial && (
                  <p className="text-xs text-amber-600 mt-1">
                    {t('partialDeliveryItems', lang)(selectedStop.receipts?.items?.length || selectedStop.inventory_unit_ids?.length || 0)}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowSignatureModal(false);
                  setSelectedStop(null);
                }}
                className="p-2 bg-slate-100 rounded-lg text-slate-500 hover:bg-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              {/* Signature Canvas */}
              <div className="bg-white rounded-xl overflow-hidden mb-3 border-2 border-slate-300">
                <canvas
                  ref={canvasRef}
                  width={350}
                  height={200}
                  className="w-full touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>

              <button
                onClick={clearSignature}
                className="w-full py-2 bg-slate-100 rounded-lg text-slate-600 text-sm mb-4 border border-slate-200"
              >
                {t('clearSignature', lang)}
              </button>

              <button
                onClick={submitDelivery}
                disabled={!hasSignature || completing}
                className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
              >
                {completing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('processing', lang)}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    {t('confirmDelivery', lang)}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-4 border-t border-slate-200 bg-white mt-4">
        <div className="text-center">
          <a 
            href="https://exitiumlabs.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center space-x-2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <span className="text-xs">{t('poweredBy', lang)}</span>
            <img src="/e.png" alt="Exitium" className="w-5 h-5 object-contain" />
            <span className="text-xs font-semibold">EXITIUM</span>
          </a>
        </div>
      </footer>
    </div>
  );
};

export default DriverView;